import { THEME_PREFERENCES, type ThemePreference } from '@erp/shared';
import type { QueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';

import { api } from './api.ts';
import { SESSION_KEY, type Session } from './session.ts';

/** Local copy read by public/theme.js before the first paint (same key). */
export const THEME_STORAGE_KEY = 'erp:theme';

const listeners = new Set<() => void>();

function isTheme(value: unknown): value is ThemePreference {
  return (THEME_PREFERENCES as readonly unknown[]).includes(value);
}

export function currentTheme(): ThemePreference {
  const value = document.documentElement.dataset.theme;
  return isTheme(value) ? value : 'system';
}

/** Applies a theme to this browser only (attribute + local copy). */
export function applyTheme(theme: ThemePreference): void {
  document.documentElement.dataset.theme = theme;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage blocked: the theme still applies until the page reloads.
  }
  for (const listener of listeners) listener();
}

export function useTheme(): ThemePreference {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    currentTheme,
    () => 'system',
  );
}

/**
 * Brings this browser in line with the saved preference after signing in (e.g. on a
 * new device). Never during an impersonation: the member's theme is not applied.
 */
export function useThemeSync(session: Session | null | undefined): void {
  const saved = session && !session.impersonation ? session.theme : null;
  useEffect(() => {
    if (saved && saved !== currentTheme()) applyTheme(saved);
  }, [saved]);
}

/**
 * Changes the theme: at once in this browser, and saved to the account unless an
 * administrator is acting as someone else (then only the local view changes).
 */
export async function chooseTheme(
  client: QueryClient,
  session: Session,
  theme: ThemePreference,
): Promise<void> {
  applyTheme(theme);
  if (session.impersonation) return;
  await api('/auth/preferences', { method: 'PATCH', body: { theme } });
  client.setQueryData<Session | null>(SESSION_KEY, (current) =>
    current ? { ...current, theme } : current,
  );
}
